"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.eventFromEnvelope = eventFromEnvelope;
exports.makeMultiplexedTransport = makeMultiplexedTransport;

var _api = require("../api.js");

var _dsn = require("../utils-hoist/dsn.js");

var _envelope = require("../utils-hoist/envelope.js");

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

/**
 * Gets an event from an envelope.
 *
 * This is only exported for use in the tests
 */
function eventFromEnvelope(env, types) {
  let event;
  (0, _envelope.forEachEnvelopeItem)(env, (item, type) => {
    if (types.includes(type)) {
      event = Array.isArray(item) ? item[1] : undefined;
    } // bail out if we found an event


    return !!event;
  });
  return event;
}
/**
 * Creates a transport that overrides the release on all events.
 */


function makeOverrideReleaseTransport(createTransport, release) {
  return options => {
    const transport = createTransport(options);
    return _objectSpread(_objectSpread({}, transport), {}, {
      send: async envelope => {
        const event = eventFromEnvelope(envelope, ['event', 'transaction', 'profile', 'replay_event']);

        if (event) {
          event.release = release;
        }

        return transport.send(envelope);
      }
    });
  };
}
/** Overrides the DSN in the envelope header  */


function overrideDsn(envelope, dsn) {
  return (0, _envelope.createEnvelope)(dsn ? _objectSpread(_objectSpread({}, envelope[0]), {}, {
    dsn
  }) : envelope[0], envelope[1]);
}
/**
 * Creates a transport that can send events to different DSNs depending on the envelope contents.
 */


function makeMultiplexedTransport(createTransport, matcher) {
  return options => {
    const fallbackTransport = createTransport(options);
    const otherTransports = new Map();

    function getTransport(dsn, release) {
      // We create a transport for every unique dsn/release combination as there may be code from multiple releases in
      // use at the same time
      const key = release ? `${dsn}:${release}` : dsn;
      let transport = otherTransports.get(key);

      if (!transport) {
        const validatedDsn = (0, _dsn.dsnFromString)(dsn);

        if (!validatedDsn) {
          return undefined;
        }

        const url = (0, _api.getEnvelopeEndpointWithUrlEncodedAuth)(validatedDsn, options.tunnel);
        transport = release ? makeOverrideReleaseTransport(createTransport, release)(_objectSpread(_objectSpread({}, options), {}, {
          url
        })) : createTransport(_objectSpread(_objectSpread({}, options), {}, {
          url
        }));
        otherTransports.set(key, transport);
      }

      return [dsn, transport];
    }

    async function send(envelope) {
      function getEvent(types) {
        const eventTypes = types && types.length ? types : ['event'];
        return eventFromEnvelope(envelope, eventTypes);
      }

      const transports = matcher({
        envelope,
        getEvent
      }).map(result => {
        if (typeof result === 'string') {
          return getTransport(result, undefined);
        } else {
          return getTransport(result.dsn, result.release);
        }
      }).filter(t => !!t); // If we have no transports to send to, use the fallback transport
      // Don't override the DSN in the header for the fallback transport. '' is falsy

      const transportsWithFallback = transports.length ? transports : [['', fallbackTransport]];
      const results = await Promise.all(transportsWithFallback.map(([dsn, transport]) => transport.send(overrideDsn(envelope, dsn))));
      return results[0];
    }

    async function flush(timeout) {
      const allTransports = [...otherTransports.values(), fallbackTransport];
      const results = await Promise.all(allTransports.map(transport => transport.flush(timeout)));
      return results.every(r => r);
    }

    return {
      send,
      flush
    };
  };
}